import React from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ReactComponent as InfoCircleSVG } from "go-core/images/svg/circle-info.svg";
import { SubscriptionEstimationApi } from "../../../api/types";

interface Props {
	featureName: string;
	featureEstimate: SubscriptionEstimationApi;
	organizationProperty: number | string | boolean;
	featureLimit?: number;
	resource?: string;
}

const SubscriptionBasedRestrictionHeaderModal = ({
	featureName,
	featureEstimate,
	organizationProperty,
	featureLimit,
	resource,
}: Props) => {
	const { t } = useTranslation();

	const getTitle = () => {
		if (isFunctionWithLimit()) {
			return `${t("lib:go_core.subscription_based_restriction.limit_reached.title", { name: featureName })}`;
		}
		if (noFunctionInBasicPlan() || trialFunctionAvailable()) {
			return (
				<p className="mb-0">
					{t("lib:go_core.subscription_based_restriction.feature_not_available_in_plan.title", {
						name: featureName,
					})}
					<span className="text-primary"> Basic</span>
				</p>
			);
		}
		if (functionNotAvailable()) {
			return t("lib:go_core.subscription_based_restriction.feature_not_available_in_subscription.title", {
				name: featureName,
			});
		}

		return "";
	};

	const getDescription = () => {
		if (isFunctionWithLimit()) {
			return (
				<p className="mb-0">
					{t("lib:go_core.subscription_based_restriction.limit_in_plan.title")}
					<span className="text-primary"> Basic </span>
					{t("lib:go_core.subscription_based_restriction.equal.title")}
					<span className="text-primary"> {featureLimit}</span>.
					{` ${t("lib:go_core.subscription_based_restriction.deactivate_feature.title", {
						name: resource,
					})}`}
					<span className="text-primary"> Pro</span>.
				</p>
			);
		}
		if (noFunctionInBasicPlan()) {
			return (
				<p className="mb-0">
					{t("lib:go_core.subscription_based_restriction.upgrade_plan.title")}
					<span className="text-primary"> Pro</span>.
				</p>
			);
		}
		if (trialFunctionAvailable()) {
			return (
				<p className="mb-0">
					{t("lib:go_core.subscription_based_restriction.upgrade_plan.title")}
					{<span className="text-primary"> Pro </span>}
					{t("lib:go_core.subscription_based_restriction.turn_trial_version.title")}
				</p>
			);
		}
		if (functionNotAvailable()) {
			return (
				<p className="mb-0">
					{t("lib:go_core.subscription_based_restriction.upgrade_plan.title")} (
					<span className="text-primary"> Pro</span>.
				</p>
			);
		}

		return "";
	};

	const isFunctionWithLimit = () => {
		return (
			featureEstimate.actual_plan &&
			featureEstimate.actual_plan.toLowerCase().includes("basic") &&
			featureEstimate.required_plan &&
			featureEstimate.required_plan.toLowerCase().includes("pro") &&
			!featureEstimate.required_addons &&
			!featureEstimate.trial_days &&
			!featureEstimate.free_of_charge &&
			(typeof organizationProperty === "number" || !isNaN(Number(organizationProperty)))
		);
	};

	const noFunctionInBasicPlan = () => {
		return (
			featureEstimate.actual_plan &&
			featureEstimate.actual_plan.toLowerCase().includes("basic") &&
			featureEstimate.required_plan &&
			featureEstimate.required_plan.toLowerCase().includes("pro") &&
			!featureEstimate.required_addons &&
			!featureEstimate.trial_days &&
			!featureEstimate.free_of_charge &&
			(typeof organizationProperty === "boolean" ||
				organizationProperty === "true" ||
				organizationProperty === "false")
		);
	};

	const trialFunctionAvailable = () => {
		return (
			featureEstimate.actual_plan &&
			featureEstimate.actual_plan.toLowerCase().includes("basic") &&
			featureEstimate.required_plan &&
			featureEstimate.required_plan.toLowerCase().includes("pro") &&
			!featureEstimate.required_addons &&
			featureEstimate.trial_days === 14 &&
			!featureEstimate.free_of_charge &&
			(typeof organizationProperty === "boolean" ||
				organizationProperty === "true" ||
				organizationProperty === "false")
		);
	};

	const functionNotAvailable = () => {
		return (
			!featureEstimate.actual_plan &&
			!featureEstimate.required_plan &&
			featureEstimate.required_addons &&
			!featureEstimate.trial_days &&
			!featureEstimate.free_of_charge
		);
	};

	return (
		<Modal.Header closeButton>
			<div className="d-flex flex-column align-items-center text-center">
				<InfoCircleSVG className="mb-4" />
				<Modal.Title>{getTitle()}</Modal.Title>
				<p className="description mt-2 mb-0">{getDescription()}</p>
			</div>
		</Modal.Header>
	);
};

export default SubscriptionBasedRestrictionHeaderModal;
